import { API, Card, Help, Pagination, Submit, Table, TextArea, ViewHeader, useGet } from '@enkla-gui'
import React, { useEffect, useState } from 'react'
import { notify, tickethelper } from '@/_helpers'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'

import EntityInfo from '@components/entity/EntityInfo'
import EntityLeaseStatus from '@components/lease/EntityLeaseStatus'
import RentalBlockStatus from '@components/entity/RentalBlockStatus'
import Events from '@components/tools/Events'
import Files from '@components/tools/Files'
import MaintenanceTable from '@components/maintenance/MaintenanceTable'
import Modal from 'react-modal'
import RentAdjustmentTable from '@components/rent_adjustment/RentAdjustmentTable'
import { icons } from '@config'

export default function Entity() {
  const { id } = useParams()
  const { data: entity, fetch: fetchEntities } = useGet({ endpoint: `api/entity/${id}` })
  const { data: children, setPage: fetchChildren } = useGet({ endpoint: `api/entity/${id}/children` })
  const { data: tickets, setPage: fetchTickets } = useGet({ endpoint: `api/entity/${id}/tickets` })

  let [deleteModal, setDeleteModal] = useState(false)
  const [stopModal, setStopModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const { register, handleSubmit, reset, formState: { errors } } = useForm()
  const history = useHistory()

  const fieldErrors = {
    ...(errors.reason ? { reason: [errors.reason.message || 'Anledning är obligatorisk'] } : {}),
    ...(error?.data || {})
  }

  useEffect(() => {
    if (!stopModal) return
    setError(null)
    reset({ reason: '' })
  }, [stopModal, reset])

  const save = data => {
    setSaving(true)
    setError(null)
    API.post(`api/entity/${id}/rental-block`, { reason: data.reason })
      .then(() => {
        notify.toast('Uthyrningsstopp har satts')
        setStopModal(false)
        setSaving(false)
        fetchEntities()
      })
      .catch(([err, res]) => {
        if (res?.data) {
          setError({ message: res.message, data: res.data })
        } else {
          notify.toast(typeof res === 'string' ? res : res?.message || 'Kunde inte spara uthyrningsstoppet', 'error')
        }
        setSaving(false)
      })
  }

  const remove = async keepChildren => {
    if (deleteModal) setDeleteModal(false)
    if (!(await notify.confirm(entity.leases.length !== 0 ? `Detta objekt har ${entity.leases.length} uthyrning(ar). Dessa kommer att raderas tillsammans med tillhörande hyresavier, filer och hyreskontrakt. Denna åtgärd går inte att ångra` : ''))) return
    API.delete(`api/entity/${id}`, { keepChildren: keepChildren })
      .then(res => {
        notify.toast(`${entity.name} borttagen`)
        history.push(entity.parent_id !== null ? `/entity/${entity.parent_id}` : `/company/${entity.company_id}`)
      })
      .catch(([err, res]) => {
        notify.toast(`Borttagningen misslyckades: ${res.message}`, 'error')
      })
  }
  useEffect(() => {
    fetchChildren()
    fetchTickets()
    fetchEntities()
  }, [id, fetchChildren, fetchTickets, fetchEntities])

  return (
    <>
      <ViewHeader
        loading={!entity}
        icon={entity?.type?.icon}
        title={entity?.name}
        subtitle={entity?.company?.name}
        breadcrumbs={entity?.breadcrumb}
        buttons={[
          { icon: 'fa-envelope', title: 'Kontakta hyresgäster', href: `#!/contact/entity/${entity?.id}` },
          { icon: 'fa-trash-alt', title: 'Radera', callback: () => (children.data.length !== 0 ? setDeleteModal(true) : remove()), position: 'right' },
          { icon: 'fa-edit', title: 'Redigera', href: `#!/entity/${entity?.id}/edit`, position: 'right' },
          ...(entity?.type?.rentable && !entity?.rental_block
            ? [{
                icon: 'fa-pause',
                title: 'Sätt uthyrningsstopp',
                callback: () => setStopModal(true),
                disabled: entity?.rented || entity?.upcoming,
                tip: 'Hur fungerar uthyrningsstopp?',
                position: 'right'
              }]
            : [])
        ]}
      />
      {entity?.type?.rentable && <EntityLeaseStatus entity={entity} />}
      {entity?.type?.rentable && <RentalBlockStatus entity={entity} refetch={fetchEntities} />}
      {entity && !entity?.type?.rentable && (
        <Card
          title='Objekt'
          icon='fas fa-fw fa-layer-group'
          buttons={{
            header: [{ link: `#!/entity/create/entity/${entity.id}`, icon: 'fas fa-plus-square ', text: 'Skapa' }]
          }}>
          <Table
            rows={children?.data}
            link={`/#!/entity/@{id}`}
            empty={{ text: 'Det finns inte några underliggande objekt', icon: 'fas fa-layer-group' }}
            functions={{
              getType: type => (
                <div class='icon-badge'>
                  <i class={`fas fa-fw ${type.icon}`} /> <span>{type.name}</span>{' '}
                </div>
              ),
                getStatus: entity =>
                  entity.type.rentable ? (
                    <span class={`w-100 badge ${entity.rented ? 'bg-success' : 'bg-default'}`}>{entity.rented ? 'Uthyrd' : 'Vakant'}</span>
                  ) : (
                    ''
                  ),
                getPrimaryTenant: entity => {
                  if (!entity.type?.rentable || !entity.rented) return ''
                  const activeLease = entity.leases?.find(l => l.state?.status === 'active' || l.state?.status === 'ending')
                  const pt = activeLease?.primary_tenant
                  return pt ? (
                    <a class='text-decoration-none' onClick={e => e.stopPropagation()} href={`/#!/tenant/${pt.id}/`}>
                      {pt.name}
                    </a>
                  ) : ''
                }
              }}
              keys={['name', 'type>function:getType', 'directchildren_count>label:light', '_this>function:getStatus', '_this>function:getPrimaryTenant']}>
              <th class='min'>Namn</th>
              <th class='min'>Typ</th>
              <th>Underliggande Objekt</th>
              <th class='min'>Status</th>
              <th class='min'>Primär hyresgäst</th>
          </Table>
          <Pagination data={children} fetch={fetchChildren} />
        </Card>
      )
      }
      <EntityInfo entity={entity} />
      <Card
        title='Ärenden'
        icon='fas fa-envelope-open-text'
        collapsed={tickets?.data?.length === 0}
        buttons={{
          header: [{ link: `#!/ticket/create/entity/${entity?.id}`, icon: 'fas fa-plus-square ', text: 'Skapa' }]
        }}>
        <Table
          rows={tickets?.data}
          link={`/#!/ticket/@{id}`}
          empty={{ text: 'Det finns inte några ärenden', icon: 'fas fa-envelope-open-text' }}
          functions={{
            getStatus: status => <span class={`badge w-100 bg-${tickethelper.status(status).color}`}>{tickethelper.status(status).label}</span>,
            tenant: data =>
              data.tenant ? (
                <a class='text-decoration-none' onClick={e => e.stopPropagation()} href={`/#!/tenant/${data.tenant.id}/`}>
                  {' '}
                  {data.tenant.name}
                </a>
              ) : (
                ''
              ),
            getType: type => <span>{tickethelper.type(type)}</span>
          }}
          keys={['status>function:getStatus', 'reference', 'type>function:getType', 'subject', 'created_at>date:full', '_this>function:tenant']}>
          <th class='min'>Status</th>
          <th class='min'>Ärendenummer</th>
          <th class='min'>Ärendetyp</th>
          <th>Ämne</th>
          <th class='min'>Skapat</th>
          <th class='min'>Hyresgäst</th>
        </Table>
        <Pagination data={tickets} fetch={fetchTickets} />
      </Card>
      <Card hidden={!entity?.type?.rentable} title='Tidigare uthyrningar' icon='fas fa-fw fa-key' collapsed={entity?.leases?.filter(l => l.state.status === 'inactive').length === 0}>
        <Table
          rows={entity?.leases?.filter(l => l.state.status === 'inactive')}
          link={`/#!/lease/@{id}`}
          empty={{ text: 'Det finns inte några avslutade uthyrningar', icon: 'fas fa-layer-group' }}
          functions={{
            status: lease => <span class={`badge bg-${lease.state.color} w-100`}>{lease.state.title}</span>,
          }}
          keys={['_this>function:status', 'primary_tenant.name', 'start>date:short', 'stop>date:short']}>
          <th class='min'>Status</th>
          <th>Primär hyresgäst</th>
          <th>Tillträddes</th>
          <th>Avslutades</th>
        </Table>
      </Card>
      {entity && <MaintenanceTable id={entity.id} type='entity' />}
      <div class='row'>
        <Events id={entity?.id} type='entity' />
        <Files id={entity?.id} type='entity' />
      </div>
      <div class='row'>
        {entity && <RentAdjustmentTable entity={entity} />}
      </div>
      <Modal isOpen={deleteModal} onRequestClose={() => setDeleteModal(false)} className='no-padding'>
        <Card
          collapsed={null}
          title='Radera Objekt'
          icon='fas fa-trash-alt'
          buttons={{
            header: [{ callback: () => setDeleteModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [
              { callback: () => remove(true), bg: 'light', text: 'Flytta Underliggande' },
              { callback: () => remove(false), bg: 'danger text-white', text: 'Radera Underliggande' }
            ]
          }}>
          <Help title='Varning' type='danger' icon='fas fa-exclamation-triangle'>
            <p>Du är på väg att radera ett objekt med underliggande objekt.</p>
            <p>
              Väljer du <b>Radera Underliggande</b> kommer <b>ALLA</b> underliggande objekt att raderas
            </p>
            <p>
              Väljer du <b>Flytta Underliggande</b> kommer alla underliggande objekt flyttas till{' '}
              <b>{entity?.parent ? entity?.parent?.name : entity?.company?.name}</b>
            </p>
          </Help>
        </Card>
      </Modal>
      <Modal isOpen={stopModal} onRequestClose={() => setStopModal(false)} className='no-padding'>
        <Card
          collapsed={null}
          title='Sätt uthyrningsstopp'
          icon='fas fa-pause'
          buttons={{
            header: [{ callback: () => setStopModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={handleSubmit(save)} text='Sätt uthyrningsstopp' sending={saving} />]
          }}>
          <TextArea
            controls={{ register }}
            name='reason'
            options={{ required: 'Anledning är obligatorisk' }}
            label='Anledning'
            rows='6'
            cols='col-12'
            placeholder='Ex: Renovering av badrummet pågår fram till den 1 september 2026'
            error={Object.keys(fieldErrors).length ? { data: fieldErrors } : null}
          />
        </Card>
      </Modal>
    </>
  )
}
